import { useFrame } from "@react-three/fiber";
import type { RefObject } from "react";
import { useRef, useState } from "react";
import type { Group, Object3D } from "three";
import {
  advanceStableBoundsTracker,
  computeProductBoundingBox,
  createStableBoundsTracker,
  hasPendingSceneAssets,
  resetStableBoundsTracker,
  type StableBoundsTracker,
} from "./productViewerCameraFit";

export type ProductSceneReadyStatus =
  | "pending-assets"
  | "empty-bounds"
  | "unstable-bounds"
  | "ready";

export type ProductSceneReadyTracker = StableBoundsTracker;

export const createProductSceneReadyTracker = createStableBoundsTracker;

export const resetProductSceneReadyTracker = resetStableBoundsTracker;

/**
 * Same readiness checks as {@link ProductViewerCamera} initial fit:
 * mesh loads finished, no loading-bound placeholders, non-empty stable bounds.
 */
export function evaluateProductSceneReady(
  object: Object3D,
  tracker: ProductSceneReadyTracker,
): ProductSceneReadyStatus {
  object.updateMatrixWorld(true);

  if (hasPendingSceneAssets(object as Group)) {
    resetProductSceneReadyTracker(tracker);
    return "pending-assets";
  }

  const currentBox = computeProductBoundingBox(object);
  if (currentBox.isEmpty()) {
    resetProductSceneReadyTracker(tracker);
    return "empty-bounds";
  }

  if (!advanceStableBoundsTracker(currentBox, tracker)) {
    return "unstable-bounds";
  }

  return "ready";
}

/**
 * Returns true while the product scene matches the main viewer's "Montage läuft"
 * completion criteria (meshes loaded, bounds stable). Stays false and can flip
 * back to false if additional asset loads start after an earlier ready state.
 */
export function useProductSceneReady(
  objectRef: RefObject<Group | null>,
  enabled = true,
): boolean {
  const trackerRef = useRef<ProductSceneReadyTracker>(
    createProductSceneReadyTracker(),
  );
  const trackedObjectRef = useRef<Group | null>(null);
  const [isReady, setIsReady] = useState(false);

  useFrame(() => {
    const object = enabled ? objectRef.current : null;
    if (trackedObjectRef.current !== object) {
      trackedObjectRef.current = object;
      resetProductSceneReadyTracker(trackerRef.current);
      setIsReady(false);
    }

    if (!object) {
      return;
    }

    const status = evaluateProductSceneReady(object, trackerRef.current);
    const ready = status === "ready";
    setIsReady((previous) => (previous === ready ? previous : ready));
  });

  return isReady;
}
